import { format } from "d3-format";
import React from "react";
import { defineMessage, type IntlShape, type MessageDescriptor, useIntl } from "react-intl";
import { ParitycountAuto } from "../charts-motion/paritycount";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_equity_fig_PARITYCOUNT";
import { datasets } from "../data/data_equity_fig_PARITYCOUNT_datasets";
import { type ChartRenderer, getLevelName, type Level, mkGemDataDecoder, ordLevel } from "../domain";
import { unsafeFromArray } from "../lib";
import { Ar, io, Ord } from "../prelude";

export * from "../data/data_equity_fig_PARITYCOUNT";

const formatValue = format(".0%");

// -----------------------------------------------------------------------------
// Parity

const parityOrder = {
  "< 80 girls/100 boys": 0,
  "80-90 girls/100 boys": 1,
  "90-97 girls/100 boys": 2,
  "gender parity": 3,
  "103-110 girls/100 boys": 4,
  "110-120 girls/100 boys": 5,
  ">120 girls/100 boys": 6,
};

const Parity = io.keyof(parityOrder);
type Parity = io.TypeOf<typeof Parity>;

function foldParity<A>(parity: Parity, pattern: { [K in Parity]: () => A }) {
  return pattern[parity]();
}

const getParityIndex = (k: Parity) => parityOrder[k];
const ordParity = Ord.contramap((parity: Parity) => getParityIndex(parity))(Ord.ordNumber);

const parityLookup: Record<Parity, MessageDescriptor> = {
  "< 80 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.<80/100",
    defaultMessage: "Fewer than 80 girls per 100 boys",
  }),
  "80-90 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.80-90/100",
    defaultMessage: "80–90 girls per 100 boys",
  }),
  "90-97 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.90-97/100",
    defaultMessage: "90–97 girls per 100 boys",
  }),
  "gender parity": defineMessage({ id: "fig.equity.PARITYCOUNT.100/100", defaultMessage: "Gender parity" }),
  "103-110 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.103-110/100",
    defaultMessage: "103–110 girls per 100 boys",
  }),
  "110-120 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.110-120/100",
    defaultMessage: "110–120 girls per 100 boys",
  }),
  ">120 girls/100 boys": defineMessage({
    id: "fig.equity.PARITYCOUNT.>120/100",
    defaultMessage: "More than 120 girls per 100 boys",
  }),
};
const getParityName = (intl: IntlShape, parity: Parity) => intl.formatMessage(parityLookup[parity]);

type ParitySide = "girl" | "boy" | "parity";
const ordParitySide = Ord.contramap((side: ParitySide) => (side === "boy" ? 0 : side === "parity" ? 1 : 2))(
  Ord.ordNumber,
);

function getParitySide(parity: Parity) {
  return foldParity<ParitySide>(parity, {
    "< 80 girls/100 boys": () => "boy",
    "80-90 girls/100 boys": () => "boy",
    "90-97 girls/100 boys": () => "boy",
    "gender parity": () => "parity",
    "103-110 girls/100 boys": () => "girl",
    "110-120 girls/100 boys": () => "girl",
    ">120 girls/100 boys": () => "girl",
  });
}

function foldParitySide<A>(side: ParitySide, pattern: { [K in ParitySide]: () => A }) {
  return pattern[side]();
}

// -----------------------------------------------------------------------------

export const Data = mkGemDataDecoder(["year", "level"], {
  value: io.number,
  parity: Parity,
  measure: io.string,
});
export type Data = io.TypeOf<typeof Data>;

export const Chart = ({ data: dataRaw }: ChartRenderer<Data>) => {
  const intl = useIntl();
  const data = React.useMemo(() => {
    const arr = dataRaw.reduce(
      (acc, x) => {
        const level_name = getLevelName(intl, x.level);
        const parity_name = getParityName(intl, x.parity);
        const parity_side = getParitySide(x.parity);
        return acc.concat(
          foldParitySide(parity_side, {
            girl: () => [{ ...x, level_name, parity_name, parity_side }],
            boy: () => [{ ...x, level_name, parity_name, parity_side }],
            parity: () => [
              {
                ...x,
                level_name,
                parity_name,
                parity_side: "girl",
                value: x.value / 2,
              },
              {
                ...x,
                level_name,
                parity_name,
                parity_side: "boy",
                value: x.value / 2,
              },
            ],
          }),
        );
      },
      [] as Array<Data[number] & { parity_name: string; parity_side: ParitySide }>,
    );

    type D = (typeof arr)[number];

    return Ar.sortBy([
      Ord.contramap((x: D) => x.level)(ordLevel),
      Ord.contramap((x: D) => x.year)(Ord.ordNumber),
      Ord.contramap((x: D) => x.parity_side)(ordParitySide),
      Ord.contramap((x: D) => x.parity)(ordParity),
    ])(arr);
  }, [intl, dataRaw]);

  const chartData = unsafeFromArray(data);
  type Datum = (typeof chartData)[number];

  const getValue = React.useCallback((x: Datum) => x.value, []);
  const getSide = React.useCallback((x: Datum) => (x.parity_side === "boy" ? "left" : "right"), []);
  const getColor = React.useCallback((x: Datum) => x.parity_name, []);
  const getYear = React.useCallback((x: Datum) => x.year, []);
  const getGroup = React.useCallback((x: Datum) => x.level, []);
  const formatGroup = React.useCallback((x: string) => getLevelName(intl, x as Level), [intl]);

  return (
    <ParitycountAuto
      data={chartData}
      getValue={getValue}
      getSide={getSide}
      getColor={getColor}
      getYear={getYear}
      getGroup={getGroup}
      formatGroup={formatGroup}
      formatValue={formatValue}
    />
  );
};

export default withFigureIO({
  url: new URL("../data/data_equity_fig_PARITYCOUNT.json", import.meta.url).href,
  csv: new URL("../data/data_equity_fig_PARITYCOUNT.zip", import.meta.url).href,
  xlsx: new URL("../data/data_equity_fig_PARITYCOUNT.xlsx", import.meta.url).href,
  metadata,
  Data,
  Chart,
  datasets,
});
